import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { api, type Categoria } from '@/lib/api';

declare const maplibregl: typeof import('maplibre-gl');

const SECTORES = ['El Paraíso', 'Las Mercedes', 'La Florida', 'La Vega', 'Antímano', 'El Valle', 'Coche', 'Caricuao'];

// ─── Campos por categoría ────────────────────────────────────────────────────
type TipoCampo = 'text' | 'radio' | 'checks';
interface Campo { key: string; label: string; tipo: TipoCampo; placeholder?: string; opciones?: string[]; required?: boolean; }

const CAMPOS: Record<string, Campo[]> = {
  salud: [
    { key: 'subcat', label: '¿Qué tipo de centro es?', tipo: 'radio', required: true,
      opciones: ['Hospital / Clínica', 'Ambulatorio / CDI', 'Farmacia / Medicamentos', 'Otro centro de salud'] },
    { key: 'nombre', label: '¿Cómo se llama?', tipo: 'text', placeholder: 'Ej: CDI El Paraíso, Hospital Vargas' },
    { key: 'estado', label: '¿Cómo está funcionando?', tipo: 'radio',
      opciones: ['Funciona bien', 'Funciona con problemas', 'Está cerrado'] },
    { key: 'fallas', label: '¿Qué falla?', tipo: 'checks',
      opciones: ['Sin médicos', 'Sin medicinas', 'Sin luz', 'Sin agua', 'Infraestructura dañada', 'Emergencias cerradas'] },
    { key: 'director', label: 'Nombre del director (opcional)', tipo: 'text', placeholder: 'Ej: Dr. Pérez' },
    { key: 'tiempo', label: '¿Cuánto tiempo lleva así?', tipo: 'radio',
      opciones: ['Hoy mismo', 'Varios días', 'Más de una semana', 'Meses'] },
  ],
  educ: [
    { key: 'subcat', label: '¿Qué nivel tiene?', tipo: 'radio', required: true,
      opciones: ['Preescolar / Maternal', 'Escuela primaria', 'Liceo / Secundaria', 'Universidad / Instituto'] },
    { key: 'nombre', label: '¿Cómo se llama?', tipo: 'text', placeholder: 'Ej: U.E. José Martí, Liceo Fermín Toro' },
    { key: 'estado', label: '¿Cómo está funcionando?', tipo: 'radio',
      opciones: ['Funciona bien', 'Con problemas', 'Cerrado'] },
    { key: 'fallas', label: '¿Qué le falta?', tipo: 'checks',
      opciones: ['Sin docentes', 'Sin comida escolar', 'Sin luz', 'Sin agua', 'Infraestructura dañada', 'Sin seguridad en la entrada'] },
    { key: 'director', label: 'Nombre del director (opcional)', tipo: 'text', placeholder: 'Ej: Lic. Rodríguez' },
    { key: 'tiempo', label: '¿Cuánto tiempo lleva así?', tipo: 'radio',
      opciones: ['Hoy mismo', 'Varios días', 'Más de una semana', 'Meses'] },
  ],
  espacios: [
    { key: 'subcat', label: '¿Qué tipo de espacio es?', tipo: 'radio', required: true,
      opciones: ['Cancha deportiva', 'Plaza / Parque', 'Área recreativa infantil', 'Jardín / Zona verde'] },
    { key: 'nombre', label: '¿Cómo se llama?', tipo: 'text', placeholder: 'Ej: Cancha El Paraíso, Plaza Bolívar' },
    { key: 'estado', label: '¿En qué estado está?', tipo: 'radio',
      opciones: ['Buena condición', 'Con algunos daños', 'Muy deteriorado', 'Abandonado'] },
    { key: 'fallas', label: '¿Qué falta o está dañado?', tipo: 'checks',
      opciones: ['Sin iluminación nocturna', 'Basura acumulada', 'Vandalismo', 'Infraestructura rota', 'Sin mantenimiento', 'Sin agua potable'] },
    { key: 'tiempo', label: '¿Cuánto tiempo lleva así?', tipo: 'radio',
      opciones: ['Hoy mismo', 'Varios días', 'Más de una semana', 'Meses'] },
  ],
  vialidad: [
    { key: 'subcat', label: '¿Cuál es el problema?', tipo: 'radio', required: true,
      opciones: ['Calle / Avenida deteriorada', 'Bache / Hueco', 'Semáforo dañado', 'Señalización vial', 'Acera / Bordillo'] },
    { key: 'via', label: '¿En qué calle o avenida?', tipo: 'text', placeholder: 'Ej: Av. Victoria, C. Las Acacias' },
    { key: 'referencia', label: 'Punto de referencia', tipo: 'text', placeholder: 'Ej: Frente al N° 22, esquina de la bodega' },
    { key: 'fallas', label: '¿Qué le pasa exactamente?', tipo: 'checks',
      opciones: ['Completamente apagado', 'Solo falla la luz de cruce', 'Señal peatonal dañada', 'Poste caído o torcido',
                 'Bache grande / peligroso', 'Hundimiento del pavimento', 'Grietas en el asfalto', 'Derrumbe en la vía', 'Sin señales de tránsito'] },
    { key: 'gravedad', label: '¿Qué tan peligroso es?', tipo: 'radio',
      opciones: ['Molestia menor', 'Hay que tener cuidado', 'Es peligroso — puede haber accidente'] },
    { key: 'tiempo', label: '¿Cuánto tiempo lleva así?', tipo: 'radio',
      opciones: ['Hoy mismo', 'Varios días', 'Más de una semana', 'Meses'] },
  ],
  servicios: [
    { key: 'subcat', label: '¿Qué servicio falla?', tipo: 'radio', required: true,
      opciones: ['Alumbrado público', 'Agua potable', 'Gas doméstico', 'Electricidad / Tendido eléctrico'] },
    { key: 'referencia', label: '¿Dónde exactamente?', tipo: 'text', placeholder: 'Ej: Av. El Paraíso entre C. 5 y C. 6, frente al N° 45' },
    { key: 'fallas', label: '¿Qué le pasa?', tipo: 'checks',
      opciones: ['Sin servicio / apagado', 'Parpadea o intermitente', 'Poste o infraestructura caída', 'Cables sueltos o expuestos',
                 'Agua sucia o con olor', 'Presión muy baja', 'Tubería rota', 'Transformador dañado', 'Sin gas en el sector'] },
    { key: 'afecta', label: '¿A cuántas personas afecta?', tipo: 'radio',
      opciones: ['Solo mi casa', 'Mi cuadra', 'Varias cuadras', 'Todo el sector'] },
    { key: 'tiempo', label: '¿Cuánto tiempo lleva así?', tipo: 'radio',
      opciones: ['Hoy mismo', 'Varios días', 'Más de una semana', 'Meses'] },
  ],
  telecom: [
    { key: 'subcat', label: '¿Qué tipo de falla es?', tipo: 'radio', required: true,
      opciones: ['Señal móvil', 'Internet / Fibra óptica', 'Televisión por cable', 'Telefonía fija', 'Ramal / Cableado en vía pública'] },
    { key: 'proveedor', label: '¿De qué empresa?', tipo: 'radio',
      opciones: ['CANTV / ABA', 'Movilnet', 'Digitel', 'Inter', 'NetUno', 'Simple TV', 'Desconocida / Otra'] },
    { key: 'referencia', label: 'Punto de referencia', tipo: 'text', placeholder: 'Ej: Poste frente al N° 30, Av. Los Mangos' },
    { key: 'fallas', label: '¿Qué pasa exactamente?', tipo: 'checks',
      opciones: ['Sin señal total', 'Señal muy lenta', 'Corte intermitente', 'Cable caído a la calle',
                 'Cable enredado en árbol', 'Poste roto', 'Cable pelado / peligroso'] },
    { key: 'afecta', label: '¿A cuántas personas afecta?', tipo: 'radio',
      opciones: ['Solo yo', 'Mi cuadra', 'Todo el sector'] },
    { key: 'tiempo', label: '¿Cuánto tiempo lleva así?', tipo: 'radio',
      opciones: ['Hoy mismo', 'Varios días', 'Más de una semana', 'Meses'] },
  ],
  ambiente: [
    { key: 'subcat', label: '¿Qué tipo de problema es?', tipo: 'radio', required: true,
      opciones: ['Basura acumulada', 'Drenaje / Cloaca tapada', 'Árbol caído / Peligroso', 'Derrame / Contaminación', 'Roedor / Plaga'] },
    { key: 'referencia', label: '¿Dónde está el problema?', tipo: 'text', placeholder: 'Ej: C. Las Acacias entre Av. Victoria y C. Boyacá' },
    { key: 'fallas', label: 'Describe el problema', tipo: 'checks',
      opciones: ['Basura acumulada varios días', 'Contenedor desbordado', 'Quema ilegal de basura',
                 'Cloaca desbordada', 'Olor fuerte', 'Árbol en peligro de caer', 'Ramas sobre cables eléctricos',
                 'Aguas negras en la calle', 'Ratas o ratones visibles', 'Mosquitos / zancudos'] },
    { key: 'riesgo', label: '¿Hay riesgo para la salud?', tipo: 'radio',
      opciones: ['Sí, es urgente', 'Posiblemente', 'No, pero hay que atenderlo'] },
    { key: 'tiempo', label: '¿Cuánto tiempo lleva así?', tipo: 'radio',
      opciones: ['Hoy mismo', 'Varios días', 'Más de una semana', 'Meses'] },
  ],
  establecimientos: [
    { key: 'subcat', label: '¿Qué tipo de local es?', tipo: 'radio', required: true,
      opciones: ['Local comercial', 'Kiosco / Buhonero', 'Supermercado / Abasto', 'Farmacia / Bodega',
                 'Centro comercial', 'Banco / Entidad financiera', 'Taller / Mecánica',
                 'Restaurante / Fonda', 'Institución pública', 'Iglesia / Centro religioso'] },
    { key: 'nombre', label: '¿Cómo se llama?', tipo: 'text', placeholder: 'Ej: Abasto El Paraíso, Banco Venezuela' },
    { key: 'estado', label: '¿Cómo está?', tipo: 'radio',
      opciones: ['Abierto y funcionando', 'Abierto con problemas', 'Cerrado temporalmente', 'Cerrado definitivamente'] },
    { key: 'fallas', label: '¿Qué está fallando?', tipo: 'checks',
      opciones: ['Cerrado sin aviso', 'Sin sistema', 'Sin efectivo / Sin punto', 'Mala atención', 'Irregularidades / Denuncias', 'Infraestructura dañada'] },
    { key: 'tiempo', label: '¿Cuánto tiempo lleva así?', tipo: 'radio',
      opciones: ['Hoy mismo', 'Varios días', 'Más de una semana', 'Meses'] },
  ],
};

// ─── Mini-mapa ───────────────────────────────────────────────────────────────
function MiniMapaPicker({ onChange }: { onChange: (lat: number, lng: number) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<InstanceType<typeof maplibregl.Map> | null>(null);
  const markerRef = useRef<InstanceType<typeof maplibregl.Marker> | null>(null);
  const [sel, setSel] = useState(false);

  useEffect(() => {
    if (mapRef.current || !containerRef.current || typeof maplibregl === 'undefined') return;
    let cancelado = false;
    async function init() {
      let sources: Record<string, any> = {};
      let glyphs = 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf';
      try {
        const ctrl = new AbortController();
        setTimeout(() => ctrl.abort(), 5000);
        const r = await fetch('https://tiles.openfreemap.org/styles/liberty', { signal: ctrl.signal });
        if (r.ok) { const b = await r.json(); sources = b.sources ?? {}; if (b.glyphs) glyphs = b.glyphs; }
      } catch { /* fondo plano */ }
      if (cancelado || !containerRef.current) return;
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: { version: 8, glyphs, sources, layers: [
          { id: 'bg', type: 'background', paint: { 'background-color': '#dde6f5' } },
          ...(sources.openmaptiles ? [
            { id: 'agua', type: 'fill' as const, source: 'openmaptiles', 'source-layer': 'water', paint: { 'fill-color': '#bbd8f5' } },
            { id: 'via', type: 'line' as const, source: 'openmaptiles', 'source-layer': 'transportation', paint: { 'line-color': '#fff', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1, 18, 5] as any } },
            { id: 'edif', type: 'fill' as const, source: 'openmaptiles', 'source-layer': 'building', paint: { 'fill-color': '#c8d5ed', 'fill-opacity': 0.7 } },
            { id: 'label', type: 'symbol' as const, source: 'openmaptiles', 'source-layer': 'transportation_name', minzoom: 14,
              layout: { 'symbol-placement': 'line' as const, 'text-field': ['get', 'name'] as any, 'text-size': 11, 'text-font': ['Noto Sans Regular'] },
              paint: { 'text-color': '#4b5563', 'text-halo-color': '#fff', 'text-halo-width': 1.5 } },
          ] : []),
        ] } as any,
        center: [-66.937, 10.483], zoom: 14, attributionControl: false,
      });
      map.addControl(new maplibregl.NavigationControl(), 'top-right');
      map.on('click', (e: any) => {
        const { lng, lat } = e.lngLat;
        onChange(lat, lng);
        setSel(true);
        map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 16), duration: 600 });
        if (markerRef.current) { markerRef.current.setLngLat([lng, lat]); }
        else {
          const el = document.createElement('div');
          el.style.cssText = 'width:32px;height:32px;border-radius:50%;background:#1D4ED8;border:4px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,0.4);cursor:grab';
          markerRef.current = new maplibregl.Marker({ element: el, draggable: true }).setLngLat([lng, lat]).addTo(map);
          markerRef.current.on('dragend', () => {
            const p = markerRef.current!.getLngLat();
            onChange(p.lat, p.lng);
          });
        }
      });
      map.getCanvas().style.cursor = 'crosshair';
      mapRef.current = map;
    }
    init();
    return () => { cancelado = true; markerRef.current = null; mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.12)', position: 'relative' }}>
      <div ref={containerRef} style={{ height: 200 }} />
      {!sel && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', background: 'rgba(10,22,40,0.5)', gap: 8 }}>
          <i className="ti ti-hand-finger" style={{ fontSize: 28, color: '#93c5fd' }} />
          <span style={{ fontSize: 15, color: '#fff', fontWeight: 700 }}>Toca el mapa aquí</span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>para poner el punto exacto</span>
        </div>
      )}
    </div>
  );
}

// ─── Wizard principal ────────────────────────────────────────────────────────
export default function BrigadistaView() {
  const { token, user } = useAuthStore();
  const qc = useQueryClient();

  const [paso, setPaso] = useState(1);
  const [cat, setCat] = useState('');
  const [extras, setExtras] = useState<Record<string, string | string[]>>({});
  const [sector, setSector] = useState(SECTORES[0]);
  const [prioridad, setPrioridad] = useState<'alta'|'media'|'baja'>('media');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [enviado, setEnviado] = useState(false);

  const { data: cats = [] } = useQuery<Categoria[]>({
    queryKey: ['categorias'],
    queryFn: () => api.categorias(token!),
    enabled: !!token,
  });

  const camposCat = cat ? (CAMPOS[cat] ?? []) : [];
  const camposReq = camposCat.filter(c => c.required);
  const paso2Ok = camposReq.every(c => {
    const v = extras[c.key]; return typeof v === 'string' ? v.trim().length > 0 : (v ?? []).length > 0;
  });

  function setExtra(key: string, val: string | string[]) {
    setExtras(prev => ({ ...prev, [key]: val }));
  }

  function buildDescripcion() {
    return camposCat.map(c => {
      const v = extras[c.key]; if (!v || (Array.isArray(v) && !v.length) || v === '') return '';
      return `${c.label}: ${Array.isArray(v) ? v.join(', ') : v}`;
    }).filter(Boolean).join('\n');
  }

  const catLabel = cats.find(c => c.slug === cat)?.label ?? '';
  const titulo = (extras['nombre'] as string)?.trim() ||
                 (extras['via'] as string)?.trim() ||
                 (extras['referencia'] as string)?.trim() ||
                 `${catLabel}${extras['subcat'] ? ` — ${extras['subcat']}` : ''}`;
  const mutation = useMutation({
    mutationFn: () => api.crearReporte(token!, {
      titulo, categoriaSlug: cat, sector,
      brigadista: user?.nombre ?? 'Brigadista', prioridad,
      descripcion: buildDescripcion(),
      ...(coords ?? {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reportes'] });
      qc.invalidateQueries({ queryKey: ['pins'] });
      setEnviado(true);
    },
  });

  function reiniciar() {
    setPaso(1); setCat(''); setExtras({}); setSector(SECTORES[0]);
    setPrioridad('media'); setCoords(null); setEnviado(false);
  }

  // ── Éxito ────────────────────────────────────────────────────────────────
  if (enviado) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#0a1628,#0d2044)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(34,197,94,0.2)', border: '3px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 36, color: '#22c55e' }}>
          <i className="ti ti-check" />
        </div>
        <h2 style={{ color: '#fff', fontWeight: 800, fontSize: 26, margin: '0 0 10px' }}>¡Listo!</h2>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 16, margin: '0 0 32px', lineHeight: 1.5 }}>El hallazgo fue enviado<br />exitosamente.</p>
        <button onClick={reiniciar}
          style={{ padding: '14px 32px', borderRadius: 14, background: '#1D4ED8', color: '#fff', fontWeight: 700, fontSize: 16 }}>
          Registrar otro
        </button>
      </div>
    </div>
  );

  const PASO_TITULO = ['', '¿Qué encontraste?', 'Cuéntanos más', 'Ubicación y datos'];
  const PASO_SUB   = ['', 'Elige el tipo de hallazgo', catLabel, 'Marca el lugar en el mapa'];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#0a1628,#0d2044)', display: 'flex', flexDirection: 'column' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ padding: '20px 20px 16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          {paso > 1 && (
            <button onClick={() => setPaso(p => p - 1)}
              style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, flexShrink: 0 }}>
              <i className="ti ti-arrow-left" />
            </button>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginBottom: 5 }}>
              Paso {paso} de 3
            </div>
            <div style={{ display: 'flex', gap: 5 }}>
              {[1,2,3].map(n => (
                <div key={n} style={{ flex: 1, height: 4, borderRadius: 99, background: n <= paso ? '#3b82f6' : 'rgba(255,255,255,0.1)', transition: 'background 300ms' }} />
              ))}
            </div>
          </div>
        </div>
        <h1 style={{ color: '#fff', fontWeight: 800, fontSize: 22, margin: '0 0 2px' }}>{PASO_TITULO[paso]}</h1>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, margin: 0 }}>{PASO_SUB[paso]}</p>
      </div>

      {/* ── Contenido ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: '0 20px 28px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

        {/* Paso 1: Categoría */}
        {paso === 1 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, flex: 1 }}>
              {cats.map(c => {
                const sel = cat === c.slug;
                return (
                  <button key={c.slug} onClick={() => { setCat(c.slug); setExtras({}); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 14px', borderRadius: 16, textAlign: 'left',
                      background: sel ? `${c.color}28` : 'rgba(255,255,255,0.06)',
                      border: `2px solid ${sel ? c.color : 'rgba(255,255,255,0.08)'}`,
                      transition: 'all 180ms' }}>
                    <i className={`ti ${c.icon}`} style={{ fontSize: 16, color: sel ? c.color : 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: sel ? '#fff' : 'rgba(255,255,255,0.6)', lineHeight: 1.2 }}>{c.label}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setPaso(2)} disabled={!cat}
              style={{ marginTop: 16, width: '100%', padding: '16px', borderRadius: 14,
                background: cat ? '#1D4ED8' : 'rgba(255,255,255,0.07)',
                color: cat ? '#fff' : 'rgba(255,255,255,0.25)',
                fontWeight: 700, fontSize: 16, cursor: cat ? 'pointer' : 'not-allowed', transition: 'all 250ms' }}>
              Siguiente →
            </button>
          </>
        )}

        {/* Paso 2: Campos específicos */}
        {paso === 2 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, flex: 1 }}>
              {camposCat.map(c => (
                <div key={c.key}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.75)', marginBottom: 10 }}>
                    {c.label}{c.required && <span style={{ color: '#f87171', marginLeft: 3 }}>*</span>}
                  </div>

                  {c.tipo === 'text' && (
                    <input value={extras[c.key] as string ?? ''} onChange={e => setExtra(c.key, e.target.value)}
                      placeholder={c.placeholder}
                      style={{ width: '100%', padding: '14px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.09)', border: '1.5px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: 15, outline: 'none', boxSizing: 'border-box' }} />
                  )}


                  {c.tipo === 'radio' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {c.opciones!.map(o => {
                        const sel = extras[c.key] === o;
                        return (
                          <button key={o} onClick={() => setExtra(c.key, o)}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderRadius: 12, textAlign: 'left',
                              background: sel ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)',
                              border: `1.5px solid ${sel ? '#3b82f6' : 'rgba(255,255,255,0.1)'}` }}>
                            <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${sel ? '#3b82f6' : 'rgba(255,255,255,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {sel && <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#3b82f6' }} />}
                            </div>
                            <span style={{ fontSize: 15, color: sel ? '#fff' : 'rgba(255,255,255,0.6)', fontWeight: sel ? 700 : 400 }}>{o}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {c.tipo === 'checks' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {c.opciones!.map(o => {
                        const vals = extras[c.key] as string[] ?? [];
                        const sel = vals.includes(o);
                        return (
                          <button key={o} onClick={() => setExtra(c.key, sel ? vals.filter(v => v !== o) : [...vals, o])}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderRadius: 12, textAlign: 'left',
                              background: sel ? 'rgba(13,148,136,0.2)' : 'rgba(255,255,255,0.05)',
                              border: `1.5px solid ${sel ? '#0d9488' : 'rgba(255,255,255,0.1)'}` }}>
                            <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${sel ? '#0d9488' : 'rgba(255,255,255,0.3)'}`, background: sel ? '#0d9488' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {sel && <i className="ti ti-check" style={{ fontSize: 11, color: '#fff' }} />}
                            </div>
                            <span style={{ fontSize: 15, color: sel ? '#fff' : 'rgba(255,255,255,0.6)', fontWeight: sel ? 700 : 400, lineHeight: 1.3 }}>{o}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button onClick={() => setPaso(3)} disabled={!paso2Ok}
              style={{ marginTop: 24, width: '100%', padding: '16px', borderRadius: 14,
                background: paso2Ok ? '#1D4ED8' : 'rgba(255,255,255,0.07)',
                color: paso2Ok ? '#fff' : 'rgba(255,255,255,0.25)',
                fontWeight: 700, fontSize: 16, cursor: paso2Ok ? 'pointer' : 'not-allowed', transition: 'all 250ms' }}>
              {paso2Ok ? 'Siguiente →' : 'Completa el campo obligatorio (*)'}
            </button>
          </>
        )}

        {/* Paso 3: Mapa + datos finales */}
        {paso === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <MiniMapaPicker onChange={(lat, lng) => setCoords({ lat, lng })} />

            {coords ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <i className="ti ti-map-pin-check" style={{ color: '#22c55e', fontSize: 16 }} />
                <span style={{ fontSize: 13, color: '#86efac', fontWeight: 600 }}>Ubicación marcada</span>
                <button onClick={() => setCoords(null)} style={{ marginLeft: 'auto', fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: '3px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }}>Borrar</button>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0, textAlign: 'center' }}>
                Sin ubicación — el reporte se guardará sin pin en el mapa.
              </p>
            )}

            {/* Sector */}
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.75)', marginBottom: 10 }}>¿En qué sector?</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {SECTORES.map(s => {
                  const sel = sector === s;
                  return (
                    <button key={s} onClick={() => setSector(s)}
                      style={{ padding: '11px 10px', borderRadius: 12, fontSize: 13, fontWeight: sel ? 700 : 400,
                        background: sel ? 'rgba(29,78,216,0.25)' : 'rgba(255,255,255,0.05)',
                        color: sel ? '#93c5fd' : 'rgba(255,255,255,0.5)',
                        border: `1.5px solid ${sel ? '#3b82f6' : 'rgba(255,255,255,0.08)'}` }}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Prioridad */}
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.75)', marginBottom: 10 }}>¿Qué tan urgente es?</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { val: 'alta' as const, label: 'Urgente', color: '#ef4444', sub: 'Peligro inmediato' },
                  { val: 'media' as const, label: 'Normal', color: '#ca8a04', sub: 'Necesita atención' },
                  { val: 'baja' as const, label: 'Puede esperar', color: '#22c55e', sub: 'Sin peligro' },
                ].map(p => {
                  const sel = prioridad === p.val;
                  return (
                    <button key={p.val} onClick={() => setPrioridad(p.val)}
                      style={{ flex: 1, padding: '12px 6px', borderRadius: 12, textAlign: 'center',
                        background: sel ? `${p.color}22` : 'rgba(255,255,255,0.05)',
                        border: `1.5px solid ${sel ? p.color : 'rgba(255,255,255,0.08)'}` }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: sel ? p.color : 'rgba(255,255,255,0.45)', marginBottom: 2 }}>{p.label}</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', lineHeight: 1.2 }}>{p.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
              style={{ width: '100%', padding: '16px', borderRadius: 14, marginTop: 4,
                background: 'linear-gradient(135deg,#0d9488,#0f766e)',
                color: '#fff', fontWeight: 700, fontSize: 16,
                boxShadow: '0 8px 24px rgba(13,148,136,0.35)', cursor: 'pointer' }}>
              {mutation.isPending ? 'Enviando…' : '✓ Enviar reporte'}
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
